import { getErrorMessage, getFilenameWithoutExtension } from '@eigtech/function-utils'
import {
  ArrayFieldRowContext,
  ArrayFieldRows,
  FormApi,
  useArrayFieldContext,
  useArrayFieldRowContext,
  useFormContext,
  useWatch,
} from '@eigtech/ui-shared-forms/shadcn'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { Card, CardContent, CardFooter } from '@eigtech/ui/components/card'
import { Dialog, DialogBackdrop, DialogContent } from '@eigtech/ui/components/dialog'
import { FieldError } from '@eigtech/ui/components/form'
import { Skeleton } from '@eigtech/ui/components/skeleton'
import { Spinner } from '@eigtech/ui/components/spinner'
import { exhaustive } from 'exhaustive'
import { Check, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { FileUploadOptions, useFileUpload } from '../../hooks/use-file-upload'
import { makeFileWithPreviewSchema } from '../../schema/file-schema'
import { GalleryDropzone, GalleryItem, GalleryToolbar } from '../gallery-file-upload'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BaseZodSchema = z.object({
  files: z.array(
    z.object({
      file: makeFileWithPreviewSchema(),
    })
  ),
})

// type BaseZodSchemaType = typeof BaseZodSchema
type BaseZodSchemaType = typeof BaseZodSchema

type BaseValues = z.infer<typeof BaseZodSchema>

export type GalleryUploadFormProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TSchema extends z.Schema<any, any>,
  TValues extends z.infer<TSchema> = z.infer<TSchema>,
> = {
  acceptedTypesLabel: string
  ArrayField: React.ComponentType<{ children: React.ReactNode }>
  numberOfFilesUploaded: number
  form: FormApi<z.infer<TSchema>>
  formId: string
  GalleryItemFields?: React.ComponentType
  isPendingUploadUrl: boolean
  isUploadError: boolean
  isUploadUrlError: boolean
  numberOfFiles: number
  uploadError: unknown
  onSubmit: (values: TValues) => Promise<void>
} & Pick<Required<FileUploadOptions>, 'maxFiles' | 'maxSize' | 'accept'>

export function GalleryUploadForm<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TSchema extends z.Schema<any, any>,
  TValues extends z.infer<TSchema> = z.infer<TSchema>,
>(props: GalleryUploadFormProps<TSchema, TValues>) {
  const [isSuccess, setIsSuccess] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      {!!isSuccess && (
        <Alert autoClose={20_000} close variant="success" onClose={() => setIsSuccess(false)}>
          <AlertIcon>
            <Check />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>Successfully uploaded files!</AlertTitle>
            <AlertDescription>
              <p>
                It may take a few minutes for them to be visible. You can go to the Documents or
                Media tab to view uploaded files and to provide additional information if needed.
              </p>
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      <GalleryUploadFormInner
        key={String(isSuccess)}
        setIsSuccess={(isSuccess) => setIsSuccess(isSuccess)}
        {...(props as unknown as GalleryUploadFormProps<BaseZodSchemaType, BaseValues>)}
      />
    </div>
  )
}

function GalleryUploadFormInner({
  accept,
  acceptedTypesLabel,
  ArrayField,
  numberOfFilesUploaded,
  form,
  formId,
  GalleryItemFields,
  isPendingUploadUrl,
  isUploadError,
  isUploadUrlError,
  maxFiles,
  maxSize,
  numberOfFiles,
  setIsSuccess,
  uploadError,
  onSubmit: onSubmitProp,
}: GalleryUploadFormProps<BaseZodSchemaType, BaseValues> & {
  setIsSuccess: (isSuccess: boolean) => void
}) {
  async function onSubmit(values: BaseValues) {
    setIsSuccess(false)

    try {
      await onSubmitProp(values)
      form.reset()
      setIsSuccess(true)
    } catch (e) {
      setIsSuccess(false)
      throw e
    }
  }

  return (
    <form.Form onSubmit={onSubmit}>
      {isUploadError && (
        <Alert variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>Something went wrong attempting to upload your documents</AlertTitle>
            <AlertDescription>
              <p>Please try again. If the issue persists, please contact EIG Customer Service.</p>
              <p>
                <span className="font-bold">Note:</span> Any files that were uploaded successfully
                have been removed and do not need to be uploaded again.
              </p>
              <p>{uploadError ? getErrorMessage(uploadError) : 'Unknown error'}</p>
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      {isUploadUrlError ? (
        <Alert variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>
              Something went wrong. If the issue persists, please contact EIG Customer Service.
            </AlertTitle>
          </AlertContent>
        </Alert>
      ) : isPendingUploadUrl ? (
        <Card>
          <CardContent>
            <Skeleton className="h-96 w-full" />
          </CardContent>
        </Card>
      ) : (
        <UploadForm
          ArrayField={ArrayField}
          GalleryItemFields={GalleryItemFields}
          accept={accept}
          acceptedTypesLabel={acceptedTypesLabel}
          form={form}
          formId={formId}
          maxFiles={maxFiles}
          maxSize={maxSize}
          numberOfFiles={numberOfFiles}
          numberOfFilesUploaded={numberOfFilesUploaded}
        />
      )}
    </form.Form>
  )
}

function UploadForm({
  accept,
  acceptedTypesLabel,
  ArrayField,
  numberOfFilesUploaded,
  form,
  formId,
  GalleryItemFields,
  maxFiles,
  maxSize,
  numberOfFiles,
}: Pick<
  GalleryUploadFormProps<BaseZodSchemaType, BaseValues>,
  | 'accept'
  | 'acceptedTypesLabel'
  | 'ArrayField'
  | 'numberOfFilesUploaded'
  | 'form'
  | 'formId'
  | 'GalleryItemFields'
  | 'maxFiles'
  | 'maxSize'
  | 'numberOfFiles'
>) {
  return (
    <>
      <div className="flex flex-col gap-4" id={formId}>
        <Card className="w-full">
          <CardContent>
            <ArrayField>
              <FilesField
                GalleryItemFields={GalleryItemFields}
                accept={accept}
                acceptedTypesLabel={acceptedTypesLabel}
                form={form}
                maxFiles={maxFiles}
                maxSize={maxSize}
              />
            </ArrayField>
          </CardContent>

          <CardFooter className="justify-end">
            <form.SubmitButton />
          </CardFooter>
        </Card>
      </div>

      <Dialog closeDisabled open={form.formState.isSubmitting}>
        <DialogBackdrop />
        <DialogContent showDismissButton={false}>
          <div className="flex flex-col gap-6 py-6">
            <Alert>
              <AlertIcon>
                <Spinner />
              </AlertIcon>
              <AlertContent>
                <AlertTitle>File upload in progress</AlertTitle>
                <AlertDescription>
                  Do not leave this page, please wait until the file upload completes. This may take
                  a few minutes depending on the size and number of files.
                </AlertDescription>
              </AlertContent>
            </Alert>

            <div className="flex flex-col gap-2">
              <div className="text-muted-foreground flex items-center justify-between text-sm">
                <span>Uploading files</span>
                <span>
                  {numberOfFilesUploaded} of {numberOfFiles}
                </span>
              </div>

              <div
                aria-valuemax={numberOfFiles || 0}
                aria-valuemin={0}
                aria-valuenow={numberOfFilesUploaded}
                className="bg-muted h-2 w-full overflow-hidden rounded-full"
                role="progressbar"
              >
                <div
                  className="bg-primary h-full rounded-full transition-[width] duration-300 ease-out"
                  style={{
                    width:
                      numberOfFiles > 0
                        ? `${Math.min(100, (numberOfFilesUploaded / numberOfFiles) * 100)}%`
                        : '0%',
                  }}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function FilesField({
  accept,
  acceptedTypesLabel,
  form,
  GalleryItemFields,
  maxFiles,
  maxSize,
}: Pick<
  GalleryUploadFormProps<BaseZodSchemaType, BaseValues>,
  'accept' | 'acceptedTypesLabel' | 'form' | 'GalleryItemFields' | 'maxFiles' | 'maxSize'
>) {
  const { control, formState } = useFormContext<BaseValues>()
  const { append, remove, update } = useArrayFieldContext()

  const filesValue = useWatch({ control, name: 'files' })
  const allFiles = useMemo(() => filesValue.map(({ file }) => file), [filesValue])

  const [
    { files, isDragging, errors },
    {
      clearFiles,
      getInputProps,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      openFileDialog,
      removeFile,
    },
  ] = useFileUpload({
    files: allFiles,
    disabled: formState.isSubmitting,
    maxSize,
    accept,
    multiple: true,
    onFilesChange(__files, context) {
      exhaustive.tag(context, 'action', {
        add({ files }) {
          append(
            files.map((file) => ({
              file,
              description: '',
              notes: '',
              title: `${getFilenameWithoutExtension(file.name)}`,
            }))
          )
        },
        clear() {
          remove()
        },
        remove({ ids }) {
          const filesToRemove = ids
            .map((id) => filesValue.findIndex((file) => file.file.id === id))
            .filter((index) => index > -1)
          remove(filesToRemove)
        },
        update({ file }) {
          const fileIndex = filesValue.findIndex((f) => f.file.id === file.id)
          const fileToUpdate = filesValue[fileIndex]
          if (fileIndex > -1) {
            update(fileIndex, {
              ...fileToUpdate,
              file,
            })
          }
        },
      })
    },
  })

  const removeFileRef = useRef(removeFile)
  removeFileRef.current = removeFile

  useEffect(() => {
    files.forEach((file) => {
      if (file.status === 'uploaded') {
        removeFileRef.current(file.id, true)
      }
    })
  }, [files])

  const renderRow = useCallback(
    (__field: ArrayFieldRowContext<BaseValues>, index: number) => {
      return (
        <FilePreview GalleryItemFields={GalleryItemFields} index={index} removeFile={removeFile} />
      )
    },
    [GalleryItemFields, removeFile]
  )

  return (
    <div className="w-full">
      {/* Upload Area */}
      {files.length < maxFiles ? (
        <GalleryDropzone
          accept={accept}
          acceptedTypesLabel={acceptedTypesLabel}
          getInputProps={getInputProps}
          handleDragEnter={handleDragEnter}
          handleDragLeave={handleDragLeave}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
          isDragging={isDragging}
          maxFiles={maxFiles}
          maxSize={maxSize}
          openFileDialog={openFileDialog}
        />
      ) : files.length > maxFiles ? (
        <Alert close variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>You have selected too many files for upload</AlertTitle>
            <AlertDescription>
              Please remove ({files.length - maxFiles}) files before submitting.
            </AlertDescription>
          </AlertContent>
        </Alert>
      ) : (
        <Alert close variant="warning">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>You have selected {maxFiles} items for upload</AlertTitle>
            <AlertDescription>
              Please submit these for upload before selecting additional items
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert className="mt-5" variant="destructive">
          <AlertIcon>
            <TriangleAlert />
          </AlertIcon>
          <AlertContent>
            <AlertTitle>File upload error(s)</AlertTitle>
            <AlertDescription className="[&_p]:mb-0">
              {errors.map((error, index) => (
                <p key={index} className="mbe-0 mb-0">
                  {error}
                </p>
              ))}
            </AlertDescription>
          </AlertContent>
        </Alert>
      )}

      {/* Gallery Stats */}
      <GalleryToolbar
        clearFiles={clearFiles}
        files={files}
        maxFiles={maxFiles}
        submitButton={<form.SubmitButton />}
      />

      {/* Image Grid */}
      {files.length > 0 && (
        <ArrayFieldRows
          RowContainer={RowContainer}
          RowsContainer={RowsContainer}
          emptyMessage=""
          renderRow={renderRow}
        />
      )}
    </div>
  )
}

function RowContainer({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>
}

function RowsContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {children}
    </div>
  )
}

function FilePreview({
  index,
  removeFile,
  GalleryItemFields,
}: {
  GalleryItemFields?: React.ComponentType
  index: number
  removeFile: (id: string) => void
}) {
  const { control, formState } = useFormContext<BaseValues>()
  const { name } = useArrayFieldRowContext<BaseValues>()

  const file = useWatch({ control, name: `${name}.file` })

  const fileError = formState.errors.files?.[index]?.file?.message

  return (
    <GalleryItem
      fileItem={file}
      footer={
        (GalleryItemFields || typeof fileError === 'string') && (
          <CardFooter className="flex-col gap-4">
            {typeof fileError === 'string' && <FieldError match>{fileError}</FieldError>}

            {GalleryItemFields && <GalleryItemFields />}
          </CardFooter>
        )
      }
      removeFile={removeFile}
    />
  )
}
